// base
import { createAsyncThunk } from '@reduxjs/toolkit';

// api
import { userApi } from '../apis/user.api';

// action
import { actions } from '../actions/user.action';
import {
  ReqCreateUser,
  ReqFetchUsers,
  ReqUpdateMobileNation,
  ResUserDetail,
  ResUsers
} from '../models/user.model';
import qs from 'qs';
import { ResultBaseProps } from 'modules/common';
import { RootState } from 'modules/rootReducer';

/**
 * 관리자 목록 조회
 */
export const getUsers = createAsyncThunk<ResUsers, void, { state: RootState }>(
  actions.GET_USERS,
  async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const request = getState().user.fetchUsersRequest;
      const query = qs.stringify(request);
      const response = await userApi.getUsers(query);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 관리자 상세 조회
 */
export const getUserDetail = createAsyncThunk<ResUserDetail, string>(
  actions.GET_USER_DETAIL,
  async (userId, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await userApi.getUsersId(userId);

      const fileId = response.resultObject.userFileId;
      if (fileId) {
        dispatch(getUserFileUrl(fileId));
      }

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 관리자 이미지 URL 조회
 */
export const getUserFileUrl = createAsyncThunk<string, string>(
  actions.GET_USER_DOWNLOAD,
  async (userId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await userApi.getUserDownloadAPI(userId);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 관리자 업데이트
 */
export const updateUser = createAsyncThunk<
  ResultBaseProps,
  'USER' | 'WORKER',
  { state: RootState }
>(actions.UPDATE_USER, async (type, { getState, fulfillWithValue, rejectWithValue }) => {
  try {
    let detail = null;

    if (type === 'USER') {
      detail = getState().user.userDetail;
    } else {
      detail = getState().worker.workerDetail;
    }

    const file = detail.file;

    const request = {
      ...detail,
      title: detail.usernameV2.title,
      firstName: detail.usernameV2.firstName,
      lastName: detail.usernameV2.lastName
    };

    const formData = new FormData();
    formData.append('user', JSON.stringify(request));

    if (file) {
      formData.append('file', file);
    }

    const response = await userApi.updateUser(formData);

    return fulfillWithValue(response);
  } catch (error) {
    return rejectWithValue(error);
  }
});

/**
 * worker 등록
 */
export const createWorker = createAsyncThunk<ResultBaseProps, void, { state: RootState }>(
  actions.CREATE_USERS_WORKER,
  async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const detail = getState().worker.workerDetail;
      const request: ReqCreateUser = {
        companyId: detail.companyId,
        mobile: detail.mobile,
        firstName: detail.usernameV2.firstName,
        lastName: detail.usernameV2.lastName,
        title: detail.usernameV2.title,
        email: detail.email,
        designationId: detail.designationId,
        birthDate: detail.birthDate,
        nationId: detail.nationId,
        mobileNationId: detail.mobileNationId,
        bloodType: detail.bloodType,
        iqamaNo: detail.iqamaNo,
        mobilization: detail.mobilization,
        role: detail.role,
        passportAssign: detail.passportAssign,
        workerType: 'WORKER'
      };

      const file = detail.file;
      const formData = new FormData();
      formData.append('user', JSON.stringify(request));

      if (file) {
        formData.append('file', file);
      }

      const response = await userApi.createWorker(formData);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 관리자가 관리자 등록
 */
export const createUser = createAsyncThunk<ResultBaseProps, void, { state: RootState }>(
  actions.CREATE_USER,
  async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const detail = getState().user.userDetail;
      const file = detail.file;

      const request: ReqCreateUser = {
        companyId: detail.companyId,
        mobile: detail.mobile,
        firstName: detail.usernameV2.firstName,
        lastName: detail.usernameV2.lastName,
        title: detail.usernameV2.title,
        email: detail.email,
        designationId: detail.designationId,
        birthDate: detail.birthDate,
        nationId: detail.nationId,
        mobileNationId: detail.mobileNationId,
        bloodType: detail.bloodType,
        iqamaNo: detail.iqamaNo,
        mobilization: detail.mobilization,
        role: detail.role,
        passportAssign: detail.passportAssign,
        workerType: 'ADMIN'
      };

      const formData = new FormData();
      formData.append('user', JSON.stringify(request));

      if (file) {
        formData.append('file', file);
      }

      const response = await userApi.createUser(formData);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 관리자 삭제
 */
export const deleteUser = createAsyncThunk<ResultBaseProps, string>(
  actions.DELETE_USER,
  async (id, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await userApi.deleteUser(id);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 관리자 data삭제
 */
export const deleteUserData = createAsyncThunk<ResultBaseProps, string[]>(
  actions.DELETE_USER_DATA,
  async (userIds, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await userApi.deleteUserData(userIds);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 관리자 검색 페이지
 * @param { company, passport, keyword, page, size } data
 * @returns { totalMap, resultList }
 */
export const getUserMetas = createAsyncThunk<ResUsers, ReqFetchUsers>(
  actions.GET_USER_METAS,
  async (data, { fulfillWithValue, rejectWithValue }) => {
    try {
      const query = qs.stringify(data);
      const response = await userApi.getUserMetas(query);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 모바일, 국가 업데이트
 */
export const updateUserMobile = createAsyncThunk<ResultBaseProps, ReqUpdateMobileNation>(
  actions.UPDATE_USER_MOBILE,
  async (data, { fulfillWithValue, rejectWithValue }) => {
    try {
      const query = qs.stringify(data);
      const response = await userApi.updateUserMobile(query);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
